import React, { useEffect, useState } from 'react'

import { TooltipV2 as Tooltip } from '@acorns/web-components'
import { theme } from '@acorns/web-components'
import styled, { css } from 'styled-components'

import {
  GIFTS_BACKDROP_OVERLAY,
  GIFTS_INTERACTIVE_GIFT_TOOLTIP,
  GIFTS_INTERACTIVE_SQUIRREL_ICON,
} from 'utils/testing/testIDs'

import { scaleIn, snap, spin } from './animations'
import { GiftShapeType } from './utils'
import { getLoadingGroup } from './utils'

export enum IconStatus {
  INITIAL = 'initial',
  DEFAULT = 'defualt',
  ACTIVE = 'active',
  VISITED = 'visited',
  REVISITED = 'revisited',
}

const Backdrop = styled.div`
  position: fixed;
  z-index: 50;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${theme.colors.slate};
  opacity: 0.5;
`

const StyledTooltip = styled(Tooltip)<{ iconStatus: IconStatus | null }>`
  align-items: center;
  background-color: white;
  color: black;
  display: flex;
  font-family: 'Avenir Next';
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  justify-content: center;
  line-height: 16px;
  padding: 6px 10px;
  text-align: center;
  width: 175px;
  ${({ iconStatus }) =>
    iconStatus === IconStatus.ACTIVE && 'position: relative'};
  ${({ iconStatus }) => iconStatus === IconStatus.ACTIVE && 'z-index: 55'};

  > div:first-child {
    &::after {
      border-top-color: white;
    }
  }

  > div:last-child {
    width: 100%;
  }
`

// TODO:
// - Overlay on initial load that highlights all squirrels and says click me or whatever the design is saying.

const IconContainer = styled.div<{
  index: number
  iconStatus: IconStatus | null
  isClockwise: boolean
  rotationStartAngle: string
  rotationEndAngle: string
}>`
  width: 100%;
  height: 100%;
  ${({ iconStatus }) =>
    (iconStatus === IconStatus.VISITED ||
      iconStatus === IconStatus.REVISITED) &&
    'opacity: 0.5'};
  ${({ iconStatus, isClockwise }) =>
    iconStatus === IconStatus.VISITED &&
    css`
      animation: ${spin('0deg', isClockwise ? '360deg' : '-360deg')} 20s linear
        infinite;
    `};
  ${({ iconStatus, rotationStartAngle, rotationEndAngle }) =>
    iconStatus === IconStatus.DEFAULT &&
    css`
      animation: ${spin(rotationStartAngle, rotationEndAngle)} 20s linear
        infinite;
    `};
  ${({ iconStatus, isClockwise }) =>
    (iconStatus === IconStatus.ACTIVE || iconStatus === IconStatus.REVISITED) &&
    css`
      animation: ${snap(isClockwise)} 0.3s cubic-bezier(0.55, -0.21, 0.14, 1.24);
    `};
  ${({ iconStatus, rotationStartAngle }) =>
    iconStatus === IconStatus.INITIAL &&
    css`
      animation: ${scaleIn(rotationStartAngle)} 0.3s
        cubic-bezier(0.23, 1.25, 0.46, 1);
    `};
  ${({ iconStatus }) =>
    iconStatus === IconStatus.ACTIVE && 'position: relative'};
  ${({ iconStatus }) => iconStatus === IconStatus.ACTIVE && 'z-index: 55'};
`

export const GiftSquirrel = ({
  index,
  gift,
  onGiftClick,
}: {
  index: number
  gift: GiftShapeType
  onGiftClick: (index: number) => void
}): React.ReactElement => {
  const [iconStatus, setIconStatus] = useState<IconStatus | null>(null)

  const handleIconClick = (index: number) => {
    switch (iconStatus) {
      case IconStatus.DEFAULT:
        setIconStatus(IconStatus.ACTIVE)
        break
      case IconStatus.ACTIVE:
        setIconStatus(IconStatus.VISITED)
        break
      case IconStatus.VISITED:
        setIconStatus(IconStatus.REVISITED)
        break
      case IconStatus.REVISITED:
        setIconStatus(IconStatus.VISITED)
        break
      default:
        break
    }
    onGiftClick(index)
  }

  const handleBackdropClick = () => {
    setIconStatus(IconStatus.VISITED)
  }

  useEffect(() => {
    let timeout2: NodeJS.Timeout
    const timeout1 = setTimeout(
      () => {
        setIconStatus(IconStatus.INITIAL)
        timeout2 = setTimeout(() => {
          setIconStatus(IconStatus.DEFAULT)
        }, 300)
      },
      getLoadingGroup(index) * 100,
    )
    return () => {
      clearTimeout(timeout1)
      clearTimeout(timeout2)
    }
  }, [index])

  const tooltipContent = gift.note
    ? `${gift.note}\n—\xa0${gift.contributorName}`
    : gift.contributorName

  return (
    <>
      {iconStatus === IconStatus.ACTIVE && (
        <Backdrop
          data-testid={GIFTS_BACKDROP_OVERLAY}
          onClick={handleBackdropClick}
        />
      )}
      <StyledTooltip
        arrow
        content={tooltipContent}
        iconStatus={iconStatus}
        key={`gift-shape-tooltip-${index}`}
        placement="top"
        testId={GIFTS_INTERACTIVE_GIFT_TOOLTIP}
        trigger="click"
      >
        <IconContainer
          isClockwise={gift.isClockwise}
          rotationStartAngle={gift.rotationStartAngle}
          rotationEndAngle={gift.rotationEndAngle}
          iconStatus={iconStatus}
          key={`gift-shape-${index}`}
          onClick={() => handleIconClick(index)}
          role="button"
          index={index}
          data-testid={GIFTS_INTERACTIVE_SQUIRREL_ICON}
          aria-label="Squirrel icon button to display gift message container"
        >
          <img
            style={{ borderRadius: '100px' }}
            alt="Squirrel icon svg"
            src={gift.squirrelIcon}
          />
        </IconContainer>
      </StyledTooltip>
    </>
  )
}
